import React from "react";

/**
 * Phase 1 — Button
 *
 * 4-tier hierarchy with soft elevation, 12px corners, and a unified
 * press animation (scale 0.98 + 150ms ease + inset shadow).
 *
 *   <Button tier="primary">  → brand-orange filled, elevated
 *   <Button tier="secondary">→ paper-2 filled, elevated, no hard border
 *   <Button tier="tertiary"> → transparent, ink-2 text
 *   <Button tier="link">     → inline text-button with underline-on-press
 *
 * Sizes:
 *   size="md" (default, h-11 = 44px tap floor)
 *   size="sm" (h-9, used in dense list-row actions)
 *
 * NOTE: This is the new design-system button. The legacy `.btn-primary`
 * / `.btn-secondary` classes in `index.css` remain in place so pages
 * not yet migrated continue to render unchanged. Do not delete them
 * until Phases 2 & 3 finish.
 */
const BASE =
  "inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-wide " +
  "rounded-button transition-all duration-150 ease-out " +
  "active:scale-[0.98] active:shadow-press " +
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 disabled:active:shadow-none " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper";

const TIERS = {
  primary:
    "bg-brand text-white shadow-rest hover:bg-brand-hover hover:shadow-raised",
  secondary:
    "bg-paper-2 text-ink shadow-rest hover:shadow-raised",
  tertiary:
    "bg-transparent text-ink-2 hover:text-ink",
  link:
    "bg-transparent text-ink-2 hover:text-ink hover:underline px-0 h-auto " +
    "tracking-normal font-mono normal-case font-medium",
};

const SIZES = {
  md: "h-11 px-5 text-xs",
  sm: "h-9 px-4 text-[11px]",
  lg: "h-14 px-6 text-sm",
};

export default function Button({
  tier = "secondary",
  size = "md",
  type = "button",
  className = "",
  children,
  ...rest
}) {
  const tierClass = TIERS[tier] || TIERS.secondary;
  // Link tier ignores size (it's inline text).
  const sizeClass = tier === "link" ? "" : (SIZES[size] || SIZES.md);
  return (
    <button
      type={type}
      className={`${BASE} ${tierClass} ${sizeClass} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
